import React, { useState, useEffect } from 'react';
import {
  Card, Button, Form, Modal, Alert, Spinner, Badge,
  Row, Col, Toast, ToastContainer
} from 'react-bootstrap';
import {
  CloudUpload, Download, CheckCircle, XCircle,
  InfoCircle, FileText, Calendar, Hash, Tag, Shield, Android2,
  ClockHistory
} from 'react-bootstrap-icons';
import { getLatestVersion, publishVersion } from '../services/versionService'; // ← Changé: plus publishVersionWithApk
import { useAuth } from '../hooks/useAuth';

export default function AppVersionManager() {
  const [latestVersion, setLatestVersion] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [alertMessage, setAlertMessage] = useState({ show: false, message: '', type: 'info' });
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const userRole = user?.user_scope;
  
  // Formulaire - SANS apk_file
  const [formData, setFormData] = useState({
    version_code: '',
    version_name: '',
    force_update: false,
    changelog: '',
    apk_url: '' // Optionnel
  });

  useEffect(() => {
    loadLatestVersion();
  }, []);

  const hideAlert = () => setAlertMessage({ show: false, message: '', type: 'info' });
  
  const showAlert = (message, type = 'warning', duration = 5000) => {
    setAlertMessage({ show: true, message, type });
    setTimeout(() => hideAlert(), duration);
  };

  const loadLatestVersion = async () => {
    setLoading(true);
    try {
      const data = await getLatestVersion();
      setLatestVersion(data);
    } catch (error) {
      console.error('Erreur:', error);
      showAlert('Erreur chargement de la version', 'danger');
    } finally {
      setLoading(false);
    }
  };

  // Submit modifié - SANS fichier
  const handleSubmit = async () => {
    if (!formData.version_code || !formData.version_name) {
      showAlert('Veuillez remplir tous les champs obligatoires', 'warning');
      return;
    }
    
    setLoading(true);
    
    try {
      const versionData = {
        version_code: parseInt(formData.version_code),
        version_name: formData.version_name,
        force_update: formData.force_update,
        changelog: formData.changelog,
        apk_url: formData.apk_url || null
      };
      
      await publishVersion(versionData);
      
      showAlert(`✅ Version ${formData.version_name} créée avec succès !`, 'success');
      setShowModal(false);
      resetForm();
      loadLatestVersion();
    } catch (error) {
      const errorMsg = error.response?.data?.message || 'Erreur lors de la création';
      showAlert(errorMsg, 'danger');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      version_code: '',
      version_name: '',
      force_update: false,
      changelog: '',
      apk_url: ''
    });
  };

  return (
    <>
      <ToastContainer className="p-3" style={{ zIndex: 9999, position: 'fixed', top: '20px', right: '20px' }}>
        {/* Toast notifications */}
      </ToastContainer>

      {alertMessage.show && (
        <div className="mb-3">
          <Alert variant={alertMessage.type} dismissible onClose={hideAlert} className="shadow-sm border-0 rounded-3">
            <div className="d-flex align-items-center">
              {alertMessage.type === 'success' ? <CheckCircle className="me-2 fs-5" /> :
               alertMessage.type === 'danger' ? <XCircle className="me-2 fs-5" /> :
               <InfoCircle className="me-2 fs-5" />}
              <span className="fw-medium">{alertMessage.message}</span>
            </div>
          </Alert>
        </div>
      )}

      {/* Header - inchangé */}
      <div className="bg-gradient-primary rounded-3 p-4 mb-4 shadow-sm">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
          <div>
            <ol className="breadcrumb mb-2">
              <li className="breadcrumb-item text-white-50">Super Admin</li>
              <li className="breadcrumb-item active text-white">Versions APK</li>
            </ol>
            <div className="d-flex align-items-center gap-3 mt-2">
              <h1 className="text-white mb-0 fs-2 fw-bold">Gestion des versions APK</h1>
              <Badge bg="info" className="d-flex align-items-center px-3 py-2 rounded-pill">
                <Android2 className="me-1" size={14} />
                Mobile
              </Badge>
            </div>
            <p className="text-white-50 mt-2 mb-0">
              Gérez les versions de l'application mobile
            </p>
          </div>
          {userRole === "global" && (
            <Button 
              variant="light" 
              onClick={() => setShowModal(true)}
              className="rounded-pill px-4 mt-3 mt-md-0"
            >
              <CloudUpload className="me-2" />
              Créer nouvelle version
            </Button>
          )}
        </div>
      </div>

      {/* Cartes statistiques - inchangé */}
      <Row className="g-4 mb-4">
        <Col md={3}>
          <div className="bg-success bg-opacity-10 rounded-3 p-3 text-center border-start border-success border-4">
            <Android2 size={32} className="text-success mb-2" />
            <div className="fs-4 fw-bold text-success">
              {latestVersion?.version_name || '1.0.0'}
            </div>
            <small className="text-muted">Version actuelle</small>
          </div>
        </Col>
        <Col md={3}>
          <div className="bg-info bg-opacity-10 rounded-3 p-3 text-center border-start border-info border-4">
            <Hash size={32} className="text-info mb-2" />
            <div className="fs-4 fw-bold text-info">
              {latestVersion?.version_code || '1'}
            </div>
            <small className="text-muted">Code version</small>
          </div>
        </Col>
        <Col md={3}>
          <div className="bg-primary bg-opacity-10 rounded-3 p-3 text-center border-start border-primary border-4">
            <Download size={32} className="text-primary mb-2" />
            <div className="fs-4 fw-bold text-primary">
              {latestVersion?.apk_url ? 'APK lié' : 'URL non définie'}
            </div>
            <small className="text-muted">Statut APK</small>
          </div>
        </Col>
        <Col md={3}>
          <div className="bg-warning bg-opacity-10 rounded-3 p-3 text-center border-start border-warning border-4">
            <ClockHistory size={32} className="text-warning mb-2" />
            <div className="fs-4 fw-bold text-warning">
              {latestVersion?.force_update ? 'Forcée' : 'Optionnelle'}
            </div>
            <small className="text-muted">Type mise à jour</small>
          </div>
        </Col>
      </Row>

      {/* Dernière version publiée - inchangé */}
      <Card className="border-0 shadow-sm rounded-4 mb-4">
        <Card.Header className="bg-white border-bottom-0 pt-4 pb-0">
          <Card.Title className="mb-0 fs-5 fw-bold text-primary">
            <Shield className="me-2" />Dernière version publiée
          </Card.Title>
        </Card.Header>
        <Card.Body className="p-4">
          {loading && !latestVersion ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
              <p className="mt-3 text-muted">Chargement...</p>
            </div>
          ) : latestVersion ? (
            <Row>
              <Col md={8}>
                <div className="bg-light p-4 rounded-3">
                  <Row className="mb-3">
                    <Col md={4}>
                      <div className="mb-3">
                        <Tag size={16} className="text-primary me-2" />
                        <strong>Version :</strong>
                        <p className="mb-0 mt-1 fs-3 fw-bold text-primary">{latestVersion.version_name}</p>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="mb-3">
                        <Hash size={16} className="text-info me-2" />
                        <strong>Code :</strong>
                        <p className="mb-0 mt-1 fs-3 fw-bold text-info">{latestVersion.version_code}</p>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div className="mb-3">
                        <Badge bg={latestVersion.force_update ? 'danger' : 'success'} className="px-3 py-2">
                          {latestVersion.force_update ? 'Mise à jour forcée' : 'Mise à jour optionnelle'}
                        </Badge>
                      </div>
                    </Col>
                  </Row>
                  {latestVersion.changelog && (
                    <div className="mt-3">
                      <FileText size={16} className="text-muted me-2" />
                      <strong>Changelog :</strong>
                      <p className="mt-2 mb-0 text-muted">{latestVersion.changelog}</p>
                    </div>
                  )}
                </div>
              </Col>
              <Col md={4} className="d-flex align-items-center">
                <Button 
                  variant="primary" 
                  size="lg"
                  href={latestVersion.apk_url || '#'}
                  download={latestVersion.apk_url ? "SyncTaxe.apk" : undefined}
                  className="rounded-pill w-100 py-3"
                  target="_blank"
                  disabled={!latestVersion.apk_url}
                >
                  <Download className="me-2" size={20} />
                  {latestVersion.apk_url ? 'Télécharger l\'APK' : 'APK non disponible'}
                </Button>
              </Col>
            </Row>
          ) : (
            <Alert variant="info" className="mb-0 rounded-3">
              <InfoCircle className="me-2" size={18} />
              Aucune version publiée
            </Alert>
          )}
        </Card.Body>
      </Card>

      {/* Modal de création - SANS upload de fichier */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered className="rounded-4">
        <Modal.Header closeButton className="border-0 pt-4 px-4 bg-primary text-white">
          <Modal.Title className="fw-bold">
            <CloudUpload className="me-2" />Créer une nouvelle version
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">
                    <Tag className="me-1" size={14} />Nom de la version *
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="ex: 1.2.0"
                    value={formData.version_name}
                    onChange={(e) => setFormData({...formData, version_name: e.target.value})}
                    className="rounded-3"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">
                    <Hash className="me-1" size={14} />Code version *
                  </Form.Label>
                  <Form.Control
                    type="number"
                    placeholder="ex: 12"
                    value={formData.version_code}
                    onChange={(e) => setFormData({...formData, version_code: e.target.value})}
                    className="rounded-3"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">
                <Download className="me-1" size={14} />URL de l'APK (optionnel)
              </Form.Label>
              <Form.Control
                type="url"
                placeholder="https://.../app.apk"
                value={formData.apk_url}
                onChange={(e) => setFormData({...formData, apk_url: e.target.value})}
                className="rounded-3"
              />
              <Form.Text className="text-muted">
                Laissez vide si vous n'avez pas encore d'URL
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="force-update-switch"
                label="Mise à jour forcée"
                checked={formData.force_update}
                onChange={(e) => setFormData({...formData, force_update: e.target.checked})}
                className="mb-2"
              />
              <Form.Text className="text-muted">
                Si activé, les utilisateurs devront obligatoirement mettre à jour
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">
                <FileText className="me-1" size={14} />Notes de version
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                placeholder="Décrivez les nouveautés..."
                value={formData.changelog}
                onChange={(e) => setFormData({...formData, changelog: e.target.value})}
                className="rounded-3"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="border-0 pb-4 px-4 justify-content-center gap-3">
          <Button variant="light" onClick={() => setShowModal(false)} className="rounded-pill px-4">
            Annuler
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSubmit}
            disabled={loading || !formData.version_code || !formData.version_name}
            className="rounded-pill px-4"
          >
            {loading ? <Spinner size="sm" className="me-2" /> : <CloudUpload className="me-2" />}
            Créer la version
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .bg-gradient-primary {
          background: linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%);
        }
        .cursor-pointer {
          cursor: pointer;
        }
        .rounded-4 {
          border-radius: 1rem !important;
        }
        .rounded-3 {
          border-radius: 0.75rem !important;
        }
      `}</style>
    </>
  );
}